import { useMemo, useState } from 'react';
import { Alert, App, Button, Checkbox, Collapse, Input, Modal, Progress, Space, Typography } from 'antd';
import { parseEmailList, type MarkInvalidResult } from '@crm/shared';
import { errorMessage } from '../../api/client';
import { useMarkInvalid, type MarkInvalidProgress } from '../../features/verification/api';
import { plural } from '../../utils/format';

/** A copyable list of addresses under a collapsible heading. */
function AddressList({ title, emails }: { title: string; emails: string[] }) {
  if (!emails.length) return null;
  return (
    <Collapse
      size="small"
      items={[
        {
          key: 'list',
          label: title,
          children: (
            <Typography.Paragraph copyable={{ text: emails.join('\n') }} style={{ maxHeight: 160, overflow: 'auto', marginBottom: 0, whiteSpace: 'pre-line', fontSize: 12 }}>
              {emails.join('\n')}
            </Typography.Paragraph>
          ),
        },
      ]}
    />
  );
}

/** Paste a list of addresses, review what matches, then mark them Invalid so campaigns skip them. */
export function MarkInvalidModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { message } = App.useApp();
  const mark = useMarkInvalid();
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<MarkInvalidResult | null>(null);
  const [progress, setProgress] = useState<MarkInvalidProgress | null>(null);
  const [block, setBlock] = useState(false);
  const emails = useMemo(() => parseEmailList(text), [text]);

  const close = () => {
    if (mark.isPending) return;
    setText('');
    setPreview(null);
    setBlock(false);
    onClose();
  };

  const run = async (apply: boolean) => {
    try {
      const r = await mark.mutateAsync({ emails, apply, block, onProgress: emails.length > 1000 ? setProgress : undefined });
      if (!apply) return setPreview(r);
      message.success(block ? `Marked ${plural(r.marked, 'email')} as invalid and blocked ${plural(r.blocked, 'address', 'addresses')} from all sending` : `Marked ${plural(r.marked, 'email')} as invalid`);
      close();
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setProgress(null);
    }
  };

  return (
    <Modal
      open={open}
      title="Mass email remover"
      width={560}
      onCancel={close}
      maskClosable={!mark.isPending}
      footer={
        preview ? (
          <Space>
            <Button onClick={() => setPreview(null)} disabled={mark.isPending}>
              Back
            </Button>
            <Button type="primary" danger disabled={!preview.marked && !preview.blocked} loading={mark.isPending} onClick={() => void run(true)}>
              {block && preview.blocked ? `Mark invalid and block ${plural(preview.blocked, 'address', 'addresses')}` : `Mark ${plural(preview.marked, 'email')} as invalid`}
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={close}>Cancel</Button>
            <Button type="primary" disabled={!emails.length} loading={mark.isPending} onClick={() => void run(false)}>
              Check {emails.length ? plural(emails.length, 'address', 'addresses') : 'list'}
            </Button>
          </Space>
        )
      }
    >
      {progress && <Progress percent={Math.round((progress.done / progress.total) * 100)} status="active" style={{ marginBottom: 12 }} />}
      {!preview ? (
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            Paste email addresses, one per line or separated by commas. Matching contacts are kept but their email is marked Invalid, so campaigns skip them.
          </Typography.Text>
          <Input.TextArea value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={'jane@example.com\njohn@example.com'} autoFocus />
          <Typography.Text type="secondary">{text.trim() ? `${plural(emails.length, 'email address', 'email addresses')} found` : ' '}</Typography.Text>
          <Checkbox checked={block} onChange={(e) => setBlock(e.target.checked)}>
            These addresses bounced: also block them from all sending
          </Checkbox>
          <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: -4 }}>
            Puts every address on the suppression list, so they are never emailed again, including addresses stored as a contact's second email, people not in the CRM yet, and anyone imported later.
          </Typography.Text>
        </Space>
      ) : (
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type={preview.marked || preview.blocked ? 'warning' : 'info'}
            showIcon
            title={preview.marked ? `${plural(preview.marked, 'contact')} will have their email marked Invalid` : preview.blocked ? 'No contact emails to mark Invalid' : 'Nothing to mark'}
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {block && <li>{preview.blocked ? `${plural(preview.blocked, 'address', 'addresses')} will be blocked from all sending (suppression list)` : 'Every address is already blocked'}</li>}
                {preview.alreadyInvalid > 0 && <li>{plural(preview.alreadyInvalid, 'address', 'addresses')} already Invalid</li>}
                {preview.suppressed > 0 && <li>{plural(preview.suppressed, 'address', 'addresses')} already Suppressed (bounced or unsubscribed), left as is</li>}
                {preview.secondary.length > 0 && <li>{plural(preview.secondary.length, 'address', 'addresses')} only found as a contact's secondary email{block ? ': blocked, not marked' : ', not changed'}</li>}
                {preview.notFound.length > 0 && <li>{plural(preview.notFound.length, 'address', 'addresses')} not found in your contacts{block ? ': blocked in case they are added later' : ''}</li>}
                {!preview.alreadyInvalid && !preview.suppressed && !preview.secondary.length && !preview.notFound.length && <li>Every address matched a contact's primary email</li>}
              </ul>
            }
          />
          <AddressList title={`Secondary emails (${preview.secondary.length})`} emails={preview.secondary} />
          <AddressList title={`Not found (${preview.notFound.length})`} emails={preview.notFound} />
        </Space>
      )}
    </Modal>
  );
}
